import { Navigate, useLocation } from "react-router-dom";
import useAuthStore from "@/store/authStore";

type RoleProtectedRouteProps = {
  children: React.ReactNode;
  allowedRoles: string[];
};

function RoleProtectedRoute({ children, allowedRoles }: RoleProtectedRouteProps) {
  const { user, status, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Wait for auth to finish initializing
  if (status !== "ready") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading session…</div>
      </div>
    );
  }

  // If not authenticated, redirect to landing/login and preserve attempted route
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{
          from: location,
        }}
      />
    );
  }

  // Check role access
  if (!user || !user.role) {
    // No user or role, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user role is in allowed roles
  // Map admin roles (admin, super_admin, manager) to "admin" for checking
  const userRole = user.role;
  const normalizedUserRole =
    userRole === "admin" || userRole === "super_admin" || userRole === "manager"
      ? "admin"
      : userRole;

  const hasAccess = allowedRoles.some((role) => {
    if (role === "admin") {
      return normalizedUserRole === "admin";
    }
    return normalizedUserRole === role;
  });

  if (!hasAccess) {
    // Redirect to appropriate dashboard based on role
    if (normalizedUserRole === "admin") {
      return <Navigate to="/dashboard/admin" replace />;
    } else if (normalizedUserRole === "agent") {
      return <Navigate to="/dashboard/agent" replace />;
    } else if (normalizedUserRole === "client") {
      return <Navigate to="/dashboard/client" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default RoleProtectedRoute;

